import { useMemo } from 'react';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useAsyncProcessor } from 'context/AsyncProcessorContext/AsyncProcessorContext';
import { CAP_TABLE_ASYNC_JOB_TITLES, CAP_TABLE_GENERATION_JOB_TITLE } from 'services/generationJobs';

export { CAP_TABLE_GENERATION_JOB_TITLE };

/**
 * Pulls the document count out of the Generate drawer's "Processing N document(s)"
 * description. Anchored to that exact producer format so digits inside a job id
 * (the app-load producer registers jobs as "Processing job <uuid>") are not
 * mistaken for a count.
 */
const parseDocumentCount = (description?: string): number | undefined => {
  const match = description?.match(/Processing (\d+) document/);
  return match ? Number(match[1]) : undefined;
};

export interface ActiveCapTableGeneration {
  /** True while at least one cap-table generation job is being processed. */
  isGenerating: boolean;
  /** Count of active cap-table generation jobs. */
  activeCount: number;
  /** Document count from the active job's details, when it is parseable. */
  documentCount?: number;
}

/**
 * Derives the active cap-table generation state from the AsyncProcessor.
 *
 * A job is present in `processingJobs` only while it is still active (it is
 * removed on a terminal status), so presence alone signals "in progress". We
 * match on the title because the async job shape has no processing type, and we
 * accept both generation and reprocess titles since both ingest the same PDFs.
 * Jobs are also matched on `companyId` (every cap-table producer tags it) so a
 * job for one company never shows as in-progress on another company's page.
 */
export const useActiveCapTableGeneration = (): ActiveCapTableGeneration => {
  const { processingJobs } = useAsyncProcessor();
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const activeCompanyId = storeValue.companyInfo?.id?.toString();

  return useMemo(() => {
    const capTableJobs = processingJobs.filter(
      job => CAP_TABLE_ASYNC_JOB_TITLES.includes(job.details.title) && job.companyId === activeCompanyId
    );
    return {
      isGenerating: capTableJobs.length > 0,
      activeCount: capTableJobs.length,
      documentCount: parseDocumentCount(capTableJobs[0]?.details.description),
    };
  }, [processingJobs, activeCompanyId]);
};

export default useActiveCapTableGeneration;
